const GRACE_PERIOD_MS = 5500;

const END_BUFFER_MS = 4000;

interface SessionOverInputs {
  isConversationOver: boolean;
  isSpeaking: boolean;
  hasPendingAudio: boolean;
  isRecording: boolean;
  isFinalizingRecording: boolean;
  isAiVoiceEnabled: boolean;
}

interface SessionOverDeps {
  endRecording: () => void;
  markEnded: () => void;
  getHasEnded: () => boolean;
}

function depsEqual<T extends readonly unknown[]>(a: T, b: T): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

export function createSessionOverController(deps: SessionOverDeps) {
  let gracePeriodElapsed = false;
  let isSessionOverLatched = false;
  let hasScheduled = false;
  let graceTimeoutId: ReturnType<typeof setTimeout> | null = null;
  let endBufferTimeoutId: ReturnType<typeof setTimeout> | null = null;
  const listeners = new Set<() => void>();

  let latestInputs: SessionOverInputs = {
    isConversationOver: false,
    isSpeaking: false,
    hasPendingAudio: false,
    isRecording: false,
    isFinalizingRecording: false,
    isAiVoiceEnabled: false,
  };
  let latchDeps: readonly [boolean, boolean] | null = null;
  let resetDeps: readonly [boolean] | null = null;
  let graceDeps: readonly [boolean, boolean] | null = null;
  let endDeps: readonly [boolean, boolean, boolean] | null = null;

  function notify() {
    listeners.forEach((listener) => listener());
  }

  function clearGraceTimeout() {
    if (graceTimeoutId !== null) {
      clearTimeout(graceTimeoutId);
      graceTimeoutId = null;
    }
  }

  function clearEndBufferTimeout() {
    if (endBufferTimeoutId !== null) {
      clearTimeout(endBufferTimeoutId);
      endBufferTimeoutId = null;
    }
  }

  function getIsSessionOver() {
    return isSessionOverLatched;
  }

  function computeIsSessionOverRaw(inputs: SessionOverInputs) {
    const aiFinishedSpeaking = !inputs.isSpeaking && !inputs.hasPendingAudio;
    return (
      inputs.isConversationOver &&
      gracePeriodElapsed &&
      (!inputs.isAiVoiceEnabled || aiFinishedSpeaking)
    );
  }

  function runLatchPhase(
    isSessionOverRaw: boolean,
    isConversationOver: boolean
  ) {
    const nextDeps = [isSessionOverRaw, isConversationOver] as const;
    if (latchDeps && depsEqual(latchDeps, nextDeps)) return;
    latchDeps = nextDeps;

    if (isSessionOverRaw) {
      queueMicrotask(() => {
        isSessionOverLatched = true;
        notify();
        reevaluate();
      });
    }
    if (!isConversationOver) {
      queueMicrotask(() => {
        isSessionOverLatched = false;
        notify();
        reevaluate();
      });
    }
  }

  function runResetPhase(isConversationOver: boolean) {
    const nextDeps = [isConversationOver] as const;
    if (resetDeps && depsEqual(resetDeps, nextDeps)) return;
    resetDeps = nextDeps;

    if (!isConversationOver) {
      queueMicrotask(() => {
        gracePeriodElapsed = false;
        notify();
        reevaluate();
      });
      hasScheduled = false;
    }
  }

  function runGracePhase(isConversationOver: boolean) {
    const nextDeps = [isConversationOver, gracePeriodElapsed] as const;
    if (graceDeps && depsEqual(graceDeps, nextDeps)) return;
    graceDeps = nextDeps;

    clearGraceTimeout();
    if (!isConversationOver || gracePeriodElapsed) return;

    graceTimeoutId = setTimeout(() => {
      graceTimeoutId = null;
      gracePeriodElapsed = true;
      notify();
      reevaluate();
    }, GRACE_PERIOD_MS);
  }

  function runEndPhase(inputs: SessionOverInputs, isSessionOverRaw: boolean) {
    const isSessionOver = isSessionOverLatched || isSessionOverRaw;
    const nextDeps = [
      isSessionOver,
      inputs.isRecording,
      inputs.isFinalizingRecording,
    ] as const;
    if (endDeps && depsEqual(endDeps, nextDeps)) return;
    endDeps = nextDeps;

    const canEnd =
      isSessionOver &&
      inputs.isRecording &&
      !deps.getHasEnded() &&
      !hasScheduled &&
      !inputs.isFinalizingRecording;

    if (!canEnd) return;

    hasScheduled = true;
    deps.markEnded();

    endBufferTimeoutId = setTimeout(() => {
      endBufferTimeoutId = null;
      deps.endRecording();
    }, END_BUFFER_MS);
  }

  function reevaluate() {
    const isSessionOverRaw = computeIsSessionOverRaw(latestInputs);

    runLatchPhase(isSessionOverRaw, latestInputs.isConversationOver);
    runResetPhase(latestInputs.isConversationOver);
    runGracePhase(latestInputs.isConversationOver);
    runEndPhase(latestInputs, isSessionOverRaw);
  }

  function update(inputs: SessionOverInputs) {
    latestInputs = inputs;
    reevaluate();
  }

  function subscribe(listener: () => void) {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }

  function dispose() {
    clearGraceTimeout();
    clearEndBufferTimeout();
    listeners.clear();
  }

  return { update, subscribe, getIsSessionOver, dispose };
}
